import { createAsyncThunk, createSelector, createSlice } from "@reduxjs/toolkit"
import { generatePasses, getPasses } from "./api"
import { allowUpdate } from "../../app/store"

const initialState = {
  byId: {}, // A map of passes by id
  matchesByCtx: {
    // root context powering the passes list view
    root: {
      _loadStatus: "idle", // Information about the loading status of a call
      _errorMessage: undefined,
      ids: [], // passes by id
      page: {}, // Pagination information
    },
    /*
      Example of other contexts
      "rso/123": {
        ids: [],
        page: {}, // Pagination information
      },
    */
  },
  generating: {}, // space for status of generate passes requests
}

/**
 * Thunk to perform async logic to fetch the passes matching the provided filters
 *
 * @type {AsyncThunk<*, {readonly filter?: *, readonly context?: *, readonly page?: *}, {}>}
 */
export const fetchPasses = createAsyncThunk(
  "passes/fetch",
  /**
   *
   * @param filter the filters to apply to the passes search
   * @param page Pagination information
   * @param context The context from where the query is being made.  If its from the passes list view then root otherwise examples could be `observation/123`
   * @returns {Promise<*>}
   */
  async ({ filter, page, context }) => {
    const response = await getPasses({
      filter,
      page,
    })
    if (context) {
      response.context = context
    }
    return response
  }
)

/**
 * Thunk to perform async logic to generate passes for a list of objects
 * @type {AsyncThunk<*, void, {}>}
 */
export const updatePasses = createAsyncThunk(
  "passes/generate",
  async ({ norad_ids, from, to }, { rejectWithValue }) => {
    try {
      return await generatePasses({
        norad_ids,
        from,
        to,
      })
    } catch (error) {
      throw rejectWithValue(error.message)
    }
  }
)

/**
 * Passes reducer
 */
export const passesSlice = createSlice({
  name: "passes",
  initialState,
  // The `reducers` field lets us define reducers and generate associated actions
  reducers: {
    clearMatches: (state, action) => {
      state.matchesByCtx[action?.payload || "root"].ids = []
    },
  },
  // The `extraReducers` field lets the slice handle actions defined elsewhere,
  // including actions generated by createAsyncThunk or in other slices.
  extraReducers: (builder) => {
    builder
      .addCase(fetchPasses.pending, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (!state.matchesByCtx[context]) {
          state.matchesByCtx[context] = {
            ids: [],
            page: {},
          }
        }
        state.matchesByCtx[context]._loadStatus = "loading"
      })
      .addCase(fetchPasses.fulfilled, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        if (state.matchesByCtx[context]) {
          state.matchesByCtx[context]._loadStatus = "idle"
          if (allowUpdate(state.matchesByCtx[context].at, action.payload.at)) {
            // Don't reset if we are paging
            if (action?.payload?.page?.start_index === 0) {
              state.matchesByCtx[context].ids = []
            }
            action?.payload?.data?.map((pass) => {
              // Add the passes to state by id (this can include ids not retrieved in this call)
              state.byId[pass.id] = {
                ...pass,
                _at: action.payload.at,
              }
              // add the ids of the passes that match from this query
              if (state.matchesByCtx[context].ids.indexOf(pass.id) === -1) {
                state.matchesByCtx[context].ids.push(pass.id)
              }
            })
            state.matchesByCtx[context].page = action.payload.page
            state.matchesByCtx[context].at = action.payload.at
          }
        }
      })
      .addCase(fetchPasses.rejected, (state, action) => {
        let context = action?.meta?.arg?.context || "root"
        state.matchesByCtx[context]._loadStatus = "rejected"
        state.matchesByCtx[context]._errorMessage = action?.error?.message
      })
      .addCase(updatePasses.pending, (state) => {
        state.generating._saveStatus = "updating"
      })
      .addCase(updatePasses.fulfilled, (state) => {
        state.generating._saveStatus = "idle"
      })
      .addCase(updatePasses.rejected, (state) => {
        state.generating._saveStatus = "rejected"
      })
  },
})

// Selector for all passes
export const selectPassesById = (state) => state.passes.byId

/**
 * Takes in a context parameter and returns matches from matchesByContext[context]
 * Usage in components: const items = useSelector((state) => selectPassesByContext(state, `rso/${id}`))
 */
export const selectPassesByContext = createSelector(
  [
    // first input - extract value from `state`
    (state) => state.passes.matchesByCtx,
    // Take the second arg, `context`, and forward to the output selector
    (state, context) => context,
  ],
  // Output selector gets (`passes, context)` as args
  (matchesByCtx, context) => {
    return matchesByCtx[context]
  }
)

/**
 * Takes in a list of ids as a parameter and returns matches from byIds
 * Usage in components: const items = useSelector((state) => selectPassesByIds(state, ["1", "2"]))
 */
export const selectPassesByIds = createSelector(
  [
    // first input - extract value from `state`
    selectPassesById,
    // Take the second arg, `ids`, and forward to the output selector
    (state, ids) => ids,
  ],
  // Output selector gets (`byId, ids)` as args
  (byId, ids) => {
    return ids.map((id) => byId[id] || { id })
  }
)

export const selectGenerating = (state) => state.passes.generating

export const { clearMatches } = passesSlice.actions

export default passesSlice.reducer
